import { useMemo } from "react";
import { Label } from "@fluentui/react/lib/Label";

import { WithConfiguration } from "Components/Generic/Configuration";
import { IconButtonWithTooltip } from "Components/Generic/IconButtonWithTooltip";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { WithTranslator } from "Components/Generic/Translator";
import { isTrue } from "Utils/Boolean";
import { getTranslatedFieldLabel } from "Utils/Field";
import { getItemType } from "Utils/Item";
import {
  isBoolField,
  isDateField,
  isDateTimeField,
  isDecimalField,
  isIntegerField,
  isCurrencyField,
  isMultiLineField,
  isMultiOptionListField,
  isOptionListField,
  isStringField,
  isUsersField
} from "Utils/Metadata";
import { isNotNullOrWhiteSpace } from "Utils/String";
import { generateUniqueId } from "Utils/UniqueId";
import { EditBoolean } from "./EditBoolean";
import { EditDateTime } from "./EditDateTime/EditDateTime";
import { EditMoney } from "./EditMoney";
import { EditMultiLine } from "./EditMultiLine";
import { EditNumber } from "./EditNumber/EditNumber";
import { EditOption } from "./EditOption/EditOption";
import { EditText } from "./EditText";
import { EditUser } from "./EditUser";
import { useEditFieldStyles } from "./UseEditFieldStyles";
import { IEditFieldProps } from "./IEditFieldProps";

export const EditField = WithTheme(
  WithConfiguration(
    WithTranslator((props: IEditFieldProps): JSX.Element => {
      const {
        fieldName, disableAfterExport, item, isRequired = false, translator
      } = props;
      const styles = useEditFieldStyles();
      const isExported = isTrue(item?.[`@isExported`], true);
      const disabled = disableAfterExport && isExported;
      const htmlId = useMemo(
        () => (isNotNullOrWhiteSpace(props.htmlId) ? props.htmlId : generateUniqueId(true)),
        [props.htmlId]
      );

      const labelText = getTranslatedFieldLabel(fieldName, getItemType(item), translator);

      return (
        <>
          <Label required={isRequired} htmlFor={htmlId} title={`${labelText} (${fieldName})`}>
            {labelText}
            {disabled && (
              <IconButtonWithTooltip
                className={styles.lockIcon}
                iconProps={{ iconName: `Lock` }}
                tooltip={translator.get(
                  `edit.locked`,
                  `Editing deactivated, as the project has already been exported.`
                )}
              />
            )}
          </Label>
          {handleField({ ...props, htmlId, disabled })}
        </>
      );
    })
  )
);

function handleField(props: IEditFieldProps): JSX.Element {
  if (isUsersField(props)) {
    return <EditUser {...props} />;
  }

  if (isStringField(props)) {
    return <EditText {...props} />;
  }

  if (isDateTimeField(props) || isDateField(props)) {
    return <EditDateTime {...props} />;
  }

  if (isBoolField(props)) {
    return <EditBoolean {...props} />;
  }

  if (isIntegerField(props) || isDecimalField(props)) {
    return <EditNumber {...props} />;
  }

  if (isCurrencyField(props)) {
    return <EditMoney {...props} />;
  }

  if (isMultiLineField(props)) {
    return <EditMultiLine {...props} />;
  }

  if (isOptionListField(props) || isMultiOptionListField(props)) {
    return <EditOption {...props} />;
  }

  return null;
}
